/**
 * ActSection — shared component for S03 (Act I), S04 (Act II), S05 (Act III).
 *
 * Full-bleed background image or video, heavy filter + vignette overlay,
 * content block with act number, phase label, hero title, story paragraph,
 * and a GOAL/CONFLICT beat.
 *
 * Each Act passes its own:
 * - sectionId (for IO + ScrollTrigger)
 * - act data from voice.ts (title, story, beat)
 * - background media (image or video)
 * - spot color token (blood / vladimir / gold)
 * - monument ghost word
 */

import { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { MonumentType } from "@/components/primitives/MonumentType";

gsap.registerPlugin(ScrollTrigger);

type ActData = {
  num: string;
  title: string;
  phase: string;
  story: string;
  beat: string;
  beatText: string;
};

type Props = {
  sectionId: "s03" | "s04" | "s05";
  meta: string;
  monument: string;
  act: ActData;
  /** Background image URL (for still-based sections) */
  imageSrc?: string;
  /** Background video URL (for S03 which uses the MP4) */
  videoSrc?: string;
  /** Poster image for video fallback */
  videoPoster?: string;
  /** Spot accent color CSS variable name — e.g. "blood", "vladimir", "gold" */
  spotColor: string;
  /** Overlay filter applied to the background image */
  bgFilter?: string;
  /** Gradient overlay color applied on top of the image */
  overlayGradient?: string;
};

export function ActSection({
  sectionId,
  meta,
  monument,
  act,
  imageSrc,
  videoSrc,
  videoPoster,
  spotColor,
  bgFilter = "contrast(1.1) brightness(0.55) saturate(0.85)",
  overlayGradient = "radial-gradient(ellipse at center, rgba(5,5,5,0.35) 0%, rgba(5,5,5,0.88) 100%)",
}: Props) {
  const sectionRef = useRef<HTMLElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const storyRef = useRef<HTMLParagraphElement | null>(null);

  // Auto-play video when section enters viewport
  useEffect(() => {
    const el = sectionRef.current;
    const video = videoRef.current;
    if (!el) return;
    if (!video) return;

    // Ensure mobile autoplay attributes are all set (React doesn't always
    // apply webkit-playsinline as an attribute even when playsInline is set)
    video.muted = true;
    video.setAttribute("muted", "");
    video.setAttribute("playsinline", "");
    video.setAttribute("webkit-playsinline", "");
    video.setAttribute("x5-playsinline", "");
    video.setAttribute("x-webkit-airplay", "allow");
    video.defaultMuted = true;

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Force load first — mobile needs data before play() can start
            if (video.readyState < 2) {
              video.load();
            }
            const playPromise = video.play();
            if (playPromise !== undefined) {
              playPromise.catch(() => {
                // Autoplay blocked — attempt on first user touch
                const onTouch = () => {
                  video.play().catch(() => {});
                  document.removeEventListener("touchstart", onTouch);
                  document.removeEventListener("click", onTouch);
                };
                document.addEventListener("touchstart", onTouch, { once: true });
                document.addEventListener("click", onTouch, { once: true });
              });
            }
          } else {
            video.pause();
          }
        });
      },
      { threshold: 0.1 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // Scroll-driven content reveal
  useEffect(() => {
    const el = sectionRef.current;
    const content = contentRef.current;
    const story = storyRef.current;
    if (!el || !content) return;

    const ctx = gsap.context(() => {
      gsap.fromTo(
        content,
        { opacity: 0, y: 40 },
        {
          opacity: 1,
          y: 0,
          duration: 1,
          ease: "power3.out",
          scrollTrigger: {
            trigger: el,
            start: "top 75%",
            end: "top 25%",
            scrub: 1,
          },
        },
      );
      if (story) {
        gsap.fromTo(
          story,
          { opacity: 0, y: 20 },
          {
            opacity: 1,
            y: 0,
            duration: 1,
            ease: "power2.out",
            scrollTrigger: {
              trigger: story,
              start: "top 85%",
              end: "top 55%",
              scrub: 1,
            },
          },
        );
      }
    }, el);
    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      id={sectionId}
      className="section"
      style={{
        backgroundColor: "var(--ink)",
        color: "var(--bone)",
        overflow: "hidden",
      }}
    >
      {/* Background — video OR image */}
      {videoSrc ? (
        <video
          ref={videoRef}
          src={videoSrc}
          poster={videoPoster}
          muted
          loop
          playsInline
          autoPlay
          preload="auto"
          disableRemotePlayback
          aria-hidden="true"
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            filter: bgFilter,
            zIndex: 0,
          }}
        />
      ) : imageSrc ? (
        <img
          src={imageSrc}
          alt=""
          aria-hidden="true"
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            filter: bgFilter,
            zIndex: 0,
          }}
        />
      ) : null}

      {/* Vignette / gradient overlay */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: 0,
          background: overlayGradient,
          pointerEvents: "none",
          zIndex: 1,
        }}
      />

      {/* Spot-color wash accent (subtle) */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: 0,
          background: `linear-gradient(135deg, var(--${spotColor}) 0%, transparent 60%)`,
          opacity: 0.22,
          mixBlendMode: "multiply",
          pointerEvents: "none",
          zIndex: 2,
        }}
      />

      <MonumentType word={monument} theme="ink" />

      {/* Content block */}
      <div
        ref={contentRef}
        style={{
          position: "relative",
          zIndex: 5,
          padding: "var(--space-lg)",
          maxWidth: "min(960px, 88vw)",
          margin: "0 auto",
          textAlign: "center",
        }}
      >
        {/* Act number + phase */}
        <div
          className="type-meta-sm"
          style={{
            color: `var(--${spotColor})`,
            marginBottom: "var(--space-md)",
            letterSpacing: "var(--ls-meta-wide)",
          }}
        >
          {meta}
        </div>

        <div
          className="type-meta-lg"
          style={{
            color: "var(--bone)",
            opacity: 0.7,
            marginBottom: "var(--space-sm)",
            letterSpacing: "var(--ls-meta-wide)",
          }}
        >
          {act.num} &middot; {act.phase}
        </div>

        {/* Hero title */}
        <h2
          className="type-hero-xl"
          style={{
            color: "var(--bone)",
            margin: 0,
            marginBottom: "var(--space-lg)",
            textShadow: "0 4px 40px rgba(0,0,0,0.9)",
          }}
        >
          {act.title}
        </h2>

        {/* Story paragraph */}
        <p
          ref={storyRef}
          style={{
            fontFamily: "var(--f-serif-italic)",
            fontSize: "clamp(1rem, 1.35vw, 1.3125rem)",
            fontStyle: "italic",
            lineHeight: 1.55,
            color: "var(--bone)",
            opacity: 0.92,
            margin: "0 auto",
            marginBottom: "var(--space-lg)",
            maxWidth: "58ch",
            textShadow: "0 2px 20px rgba(0,0,0,0.95)",
          }}
        >
          {act.story}
        </p>

        {/* Beat separator + label + text */}
        <div
          style={{
            borderTop: `1px solid var(--${spotColor})`,
            paddingTop: "var(--space-md)",
            maxWidth: "50ch",
            margin: "0 auto",
          }}
        >
          <div
            className="type-meta-sm"
            style={{
              color: `var(--${spotColor})`,
              letterSpacing: "var(--ls-meta-wide)",
              marginBottom: "var(--space-xs)",
            }}
          >
            {act.beat}
          </div>
          <div
            style={{
              fontFamily: "var(--f-serif-body)",
              fontSize: "clamp(0.9375rem, 1.1vw, 1.125rem)",
              lineHeight: 1.5,
              color: "var(--bone)",
              textShadow: "0 2px 16px rgba(0,0,0,0.9)",
            }}
          >
            {act.beatText}
          </div>
        </div>
      </div>
    </section>
  );
}
